import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Leaf, ArrowLeft, Plus, BookOpen, Trash2, Edit, Calendar, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import ChildSelector from '@/components/ChildSelector';
import FamilyLink from '@/components/FamilyLink';
import BrandMark from '@/components/BrandMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY_OPTIONS = [
  { value: 'independent', label: 'Independent (read by themselves)' },
  { value: 'shared', label: 'Shared (read together)' },
  { value: 'read_aloud', label: 'Read-aloud (parent reading)' },
];

const EMPTY_FORM = {
  title: '', author: '', date_started: '', date_finished: '',
  pages: '', difficulty: '', child_response: '', parent_notes: '',
};

function ReadingForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const payload = {
      ...form,
      title: form.title.trim(),
      author: form.author.trim(),
      pages: form.pages ? parseInt(form.pages, 10) : null,
      date_started: form.date_started || null,
      date_finished: form.date_finished || null,
      difficulty: form.difficulty || null,
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="title">Title <span className="text-red-500">*</span></Label>
          <Input data-testid="reading-title" id="title" value={form.title}
            onChange={e => set('title', e.target.value)} required className="rounded-xl mt-1" />
        </div>
        <div>
          <Label htmlFor="author">Author</Label>
          <Input data-testid="reading-author" id="author" value={form.author}
            onChange={e => set('author', e.target.value)} className="rounded-xl mt-1" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="date_started">Started</Label>
          <Input data-testid="reading-started-input" id="date_started" type="date" value={form.date_started || ''}
            onChange={e => set('date_started', e.target.value)} className="rounded-xl mt-1" />
        </div>
        <div>
          <Label htmlFor="date_finished">Finished</Label>
          <Input data-testid="reading-finished-input" id="date_finished" type="date" value={form.date_finished || ''}
            onChange={e => set('date_finished', e.target.value)} className="rounded-xl mt-1" />
        </div>
        <div>
          <Label htmlFor="pages">Pages</Label>
          <Input data-testid="reading-pages-input" id="pages" type="number" min="0" value={form.pages || ''}
            onChange={e => set('pages', e.target.value)} className="rounded-xl mt-1" />
        </div>
      </div>
      <div>
        <Label>Reading Mode</Label>
        <Select value={form.difficulty || ''} onValueChange={v => set('difficulty', v)}>
          <SelectTrigger data-testid="reading-difficulty" className="rounded-xl mt-1"><SelectValue placeholder="How was this book read?" /></SelectTrigger>
          <SelectContent>
            {DIFFICULTY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="child_response">Child&apos;s Reflection</Label>
        <Textarea data-testid="reading-response" id="child_response" value={form.child_response}
          onChange={e => set('child_response', e.target.value)}
          placeholder="What did they think? Favourite character? Connections to their own life?"
          className="rounded-xl mt-1 min-h-[80px]" />
      </div>
      <div>
        <Label htmlFor="parent_notes">Parent / Whānau Notes</Label>
        <Textarea data-testid="reading-parent-notes" id="parent_notes" value={form.parent_notes}
          onChange={e => set('parent_notes', e.target.value)}
          placeholder="Reading observations — fluency, comprehension, vocabulary..."
          className="rounded-xl mt-1 min-h-[80px]" />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <Button data-testid="reading-cancel" type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button data-testid="reading-save" type="submit" disabled={saving}
          className="bg-fern hover:bg-fern-light text-white rounded-full px-6">
          {saving ? 'Saving…' : 'Save Entry'}
        </Button>
      </div>
    </form>
  );
}

export default function ReadingLogPage() {
  const { authHeaders, childParam, activeChildName, activeChild } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null = none, 'new' = creating, otherwise entry obj
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reading-log${childParam}`, { headers: authHeaders });
      setEntries(r.data || []);
    } catch { toast.error('Failed to load reading log'); }
    setLoading(false);
  }, [authHeaders, childParam]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing && editing.id) {
        await axios.put(`${API}/reading-log/${editing.id}`, payload, { headers: authHeaders });
      } else {
        await axios.post(`${API}/reading-log`, { ...payload, child: activeChildName || undefined }, { headers: authHeaders });
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this reading entry?')) return;
    try {
      await axios.delete(`${API}/reading-log/${id}`, { headers: authHeaders });
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const totalPages = entries.reduce((sum, e) => sum + (e.pages || 0), 0);
  const finishedCount = entries.filter(e => e.date_finished).length;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center">
            <BrandMark size={36} />
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/dashboard"><Button variant="ghost" size="sm" className="text-slate-600 hover:text-fern rounded-lg gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Button></Link>
            <FamilyLink />
            <ChildSelector />
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-fern-dark flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-fern" /> Reading Log
            </h1>
            <p className="text-slate-500 mt-2">Track every book read — appears in the MoE Evidence Report.</p>
            {activeChild && (
              <p data-testid="reading-active-child" className="text-sm text-fern mt-1 font-medium">
                {activeChild.name} <span className="text-slate-400 font-normal">· Year {activeChild.year_level}</span>
              </p>
            )}
          </div>
          <Button data-testid="reading-add-btn" onClick={() => setEditing('new')}
            className="bg-fern hover:bg-fern-light text-white rounded-full gap-2">
            <Plus className="w-4 h-4" /> Add Book
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <Card className="border-0 shadow-sm rounded-2xl bg-fern-50/40">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-fern" data-testid="reading-total">{entries.length}</p>
              <p className="text-xs text-fern-dark/70 uppercase tracking-wider mt-1">Books logged</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-moana-50/40">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-moana" data-testid="reading-finished">{finishedCount}</p>
              <p className="text-xs text-moana-dark/70 uppercase tracking-wider mt-1">Finished</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-2xl bg-kowhai-50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-kowhai-dark" data-testid="reading-pages-total">{totalPages}</p>
              <p className="text-xs text-kowhai-dark/70 uppercase tracking-wider mt-1">Pages read</p>
            </CardContent>
          </Card>
        </div>

        {editing && (
          <Card className="border-0 shadow-md rounded-3xl mb-8 ring-2 ring-fern/20">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-semibold text-fern-dark">
                  {editing === 'new' ? 'Add a Book' : `Edit: ${editing.title}`}
                </h3>
                <Button variant="ghost" size="icon" onClick={() => setEditing(null)} className="rounded-full"><X className="w-4 h-4" /></Button>
              </div>
              <ReadingForm
                initial={editing === 'new' ? null : editing}
                onSubmit={save}
                onCancel={() => setEditing(null)}
                saving={saving}
              />
            </CardContent>
          </Card>
        )}

        {/* Entries */}
        {loading ? (
          <p className="text-center text-slate-400 py-12">Loading…</p>
        ) : entries.length === 0 ? (
          <Card className="border-0 shadow-sm rounded-3xl">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No books logged yet.</p>
              <Button onClick={() => setEditing('new')} className="bg-fern hover:bg-fern-light text-white rounded-full gap-2">
                <Plus className="w-4 h-4" /> Add Your First Book
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {entries.map(e => {
              const diffLabel = DIFFICULTY_OPTIONS.find(o => o.value === e.difficulty)?.label.split(' ')[0] || '';
              return (
                <Card key={e.id} data-testid={`reading-entry-${e.id}`} className="border-0 shadow-sm hover:shadow-md transition-shadow rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-lg font-semibold text-fern-dark">{e.title}</h3>
                        {e.author && <p className="text-sm text-slate-500 italic">by {e.author}</p>}
                        <div className="flex flex-wrap gap-2 mt-3 text-xs">
                          {e.date_started && <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Started {e.date_started}</span>}
                          {e.date_finished && <span className="px-2.5 py-1 rounded-full bg-fern-50 text-fern-dark inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Finished {e.date_finished}</span>}
                          {e.pages && <span className="px-2.5 py-1 rounded-full bg-moana-50 text-moana-dark inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {e.pages} pages</span>}
                          {diffLabel && <span className="px-2.5 py-1 rounded-full bg-kowhai-50 text-kowhai-dark">{diffLabel}</span>}
                        </div>
                        {e.child_response && (
                          <div className="mt-3 p-3 bg-fern-50/40 rounded-xl">
                            <p className="text-xs uppercase tracking-wider text-fern-dark font-semibold mb-1">{activeChild?.name || 'Child'} said</p>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{e.child_response}</p>
                          </div>
                        )}
                        {e.parent_notes && (
                          <div className="mt-2 p-3 bg-kowhai-50/60 rounded-xl">
                            <p className="text-xs uppercase tracking-wider text-kowhai-dark font-semibold mb-1">Parent notes</p>
                            <p className="text-sm text-slate-700 whitespace-pre-line">{e.parent_notes}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button data-testid={`reading-edit-${e.id}`} variant="ghost" size="icon" onClick={() => setEditing(e)} className="rounded-xl text-slate-400 hover:text-moana"><Edit className="w-4 h-4" /></Button>
                        <Button data-testid={`reading-delete-${e.id}`} variant="ghost" size="icon" onClick={() => remove(e.id)} className="rounded-xl text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
